import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Clock, Hash, Calendar, Share2, Twitter, Linkedin, Link as LinkIcon, BookOpen, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// --- Imperative SEO helpers ---------------------------------------------------
// Manage <head> meta tags for the article reader. We do this imperatively (not
// via react-helmet-async) because Helmet 3.x doesn't reliably dedupe meta tags
// against static index.html entries, and the SPA navigates between pages without
// remount-cleanup semantics that Helmet expects. find-or-create + class-mark
// pattern keeps this idempotent and safe.
const SEO_MARK = 'data-willkart-article-meta';

const _upsertMeta = (attrName, attrValue, content) => {
  const head = document.head;
  let el = head.querySelector(`meta[${attrName}="${attrValue}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    el.setAttribute(SEO_MARK, 'true');
    head.appendChild(el);
  } else if (!el.hasAttribute(SEO_MARK)) {
    // Existing static tag — mark it as managed so cleanup can restore later.
    el.setAttribute(SEO_MARK, 'restore');
    el.setAttribute(`${SEO_MARK}-orig`, el.getAttribute('content') || '');
  }
  el.setAttribute('content', content || '');
};

const _upsertLink = (rel, href) => {
  const head = document.head;
  let el = head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute(SEO_MARK, 'true');
    head.appendChild(el);
  }
  el.setAttribute('href', href || '');
};

const _setSeoMeta = ({ title, description, url, image, publishedAt, tags }) => {
  document.title = title;
  _upsertMeta('name', 'description', description);
  _upsertLink('canonical', url);
  _upsertMeta('property', 'og:type', 'article');
  _upsertMeta('property', 'og:title', title);
  _upsertMeta('property', 'og:description', description);
  _upsertMeta('property', 'og:url', url);
  if (image) _upsertMeta('property', 'og:image', image);
  _upsertMeta('name', 'twitter:card', image ? 'summary_large_image' : 'summary');
  _upsertMeta('name', 'twitter:title', title);
  _upsertMeta('name', 'twitter:description', description);
  if (image) _upsertMeta('name', 'twitter:image', image);
  if (publishedAt) _upsertMeta('property', 'article:published_time', publishedAt);
  // Remove any prior article:tag entries, then add fresh ones.
  document.head.querySelectorAll(`meta[property="article:tag"][${SEO_MARK}]`).forEach(n => n.remove());
  (tags || []).forEach(t => {
    const m = document.createElement('meta');
    m.setAttribute('property', 'article:tag');
    m.setAttribute('content', t);
    m.setAttribute(SEO_MARK, 'true');
    document.head.appendChild(m);
  });
};

const _clearArticleSeoMeta = () => {
  // Restore any tags we co-opted (had SEO_MARK="restore"), drop the ones we created.
  document.head.querySelectorAll(`[${SEO_MARK}]`).forEach(el => {
    const mode = el.getAttribute(SEO_MARK);
    if (mode === 'restore') {
      const orig = el.getAttribute(`${SEO_MARK}-orig`);
      if (orig != null) el.setAttribute('content', orig);
      el.removeAttribute(SEO_MARK);
      el.removeAttribute(`${SEO_MARK}-orig`);
    } else {
      el.remove();
    }
  });
};
// -----------------------------------------------------------------------------

const KnowledgeArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/knowledge/articles/${slug}`);
        setArticle(data);
        // Per-article SEO: set <head> meta tags imperatively (Helmet 3.x was unreliable
        // for SPA navigations + dedup across BrowserRouter, so we manage these directly).
        _setSeoMeta({
          title: `${data.meta_title || data.title} · WillKart Knowledge`,
          description: data.meta_description || data.excerpt || '',
          url: typeof window !== 'undefined' ? window.location.href : '',
          image: data.cover_image || '',
          publishedAt: data.published_at || '',
          tags: data.hashtags || [],
        });
        // Fetch related (same hashtag, exclude self)
        if (data.hashtags?.[0]) {
          const r = await axios.get(`${API}/knowledge/articles?hashtag=${encodeURIComponent(data.hashtags[0])}`);
          setRelated((r.data.articles || []).filter(a => a.slug !== data.slug).slice(0, 3));
        }
      } catch (e) {
        if (e.response?.status === 404) {
          setArticle(null);
        } else {
          toast.error('Failed to load article');
        }
      } finally {
        setLoading(false);
      }
    })();
    // Reset scroll on slug change
    window.scrollTo(0, 0);
    return () => {
      document.title = 'WillKart';
      _clearArticleSeoMeta();
    };
  }, [slug]);

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  const shareTwitter = () => {
    const text = `${article.title} — via WillKart`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(currentUrl)}`, '_blank');
  };
  const shareLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`, '_blank');
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      toast.success('Link copied to clipboard');
    } catch (_e) {
      toast.error('Could not copy link');
    }
  };
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: article.title, text: article.excerpt, url: currentUrl }); } catch (_) {}
    } else { copyLink(); }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-500">Loading article…</div>;
  }
  if (!article) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center p-6" data-testid="article-not-found">
        <BookOpen className="w-12 h-12 text-slate-700 mb-3" />
        <h1 className="text-2xl font-semibold mb-2">Article not found</h1>
        <p className="text-slate-400 mb-6">It may have been moved, archived, or never existed.</p>
        <Link to="/knowledge"><Button className="bg-amber-500 hover:bg-amber-600 text-slate-950">Browse all articles</Button></Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="knowledge-article-page">
      {/* Top nav */}
      <header className="border-b border-slate-800/60 sticky top-0 z-30 bg-slate-950/95 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/knowledge" className="flex items-center gap-2 text-slate-400 hover:text-amber-400 transition text-sm" data-testid="back-to-knowledge-link">
            <ChevronLeft className="w-4 h-4" />All articles
          </Link>
          <div className="flex items-center gap-1">
            <button onClick={shareTwitter} className="p-2 text-slate-400 hover:text-amber-400 transition" title="Share on Twitter" data-testid="share-twitter"><Twitter className="w-4 h-4" /></button>
            <button onClick={shareLinkedIn} className="p-2 text-slate-400 hover:text-amber-400 transition" title="Share on LinkedIn" data-testid="share-linkedin"><Linkedin className="w-4 h-4" /></button>
            <button onClick={copyLink} className="p-2 text-slate-400 hover:text-amber-400 transition" title="Copy link" data-testid="share-copy"><LinkIcon className="w-4 h-4" /></button>
            {typeof navigator !== 'undefined' && navigator.share && (
              <button onClick={nativeShare} className="p-2 text-slate-400 hover:text-amber-400 transition md:hidden" title="Share" data-testid="share-native"><Share2 className="w-4 h-4" /></button>
            )}
          </div>
        </div>
      </header>

      <article className="max-w-4xl mx-auto px-6 py-12">
        {/* Hashtags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {(article.hashtags || []).map(t => (
            <Link key={t} to={`/knowledge?tag=${t}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-800/60 text-amber-300 hover:bg-slate-700 transition" data-testid={`article-hashtag-${t}`}>
              <Hash className="w-3 h-3" />{t}
            </Link>
          ))}
        </div>

        {/* Title */}
        <h1 className="text-4xl lg:text-5xl font-serif text-slate-100 leading-tight mb-4" data-testid="article-title">{article.title}</h1>

        {/* Meta */}
        <div className="flex items-center gap-3 text-sm text-slate-500 mb-8 flex-wrap">
          <span>{article.author}</span>
          <span>•</span>
          {article.published_at && (
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(article.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
          )}
          {article.views ? (<><span>•</span><span className="flex items-center gap-1"><Eye className="w-3 h-3" />{article.views} views</span></>) : null}
        </div>

        {/* Cover */}
        {article.cover_image && (
          <div className="rounded-2xl overflow-hidden border border-slate-800 mb-10">
            <img src={article.cover_image} alt={article.title} className="w-full h-auto" />
          </div>
        )}

        {/* Body — rendered HTML from Tiptap. Prose styles match the editor preview. */}
        <div
          className="prose prose-invert prose-lg max-w-none prose-headings:text-slate-100 prose-headings:font-serif prose-p:text-slate-300 prose-p:leading-relaxed prose-strong:text-slate-100 prose-a:text-amber-400 prose-blockquote:text-slate-400 prose-blockquote:border-amber-500/50 prose-img:rounded-xl prose-img:my-6 prose-li:text-slate-300"
          dangerouslySetInnerHTML={{ __html: article.body_html }}
          data-testid="article-body"
        />

        {/* Bottom share */}
        <div className="mt-16 pt-8 border-t border-slate-800 flex items-center justify-between flex-wrap gap-4">
          <span className="text-sm text-slate-500">Share this article</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={shareTwitter} className="border-slate-700 text-slate-300 hover:text-amber-400" data-testid="share-bottom-twitter"><Twitter className="w-4 h-4 mr-1" />Twitter</Button>
            <Button variant="outline" size="sm" onClick={shareLinkedIn} className="border-slate-700 text-slate-300 hover:text-amber-400" data-testid="share-bottom-linkedin"><Linkedin className="w-4 h-4 mr-1" />LinkedIn</Button>
            <Button variant="outline" size="sm" onClick={copyLink} className="border-slate-700 text-slate-300 hover:text-amber-400" data-testid="share-bottom-copy"><LinkIcon className="w-4 h-4 mr-1" />Copy link</Button>
          </div>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-4xl mx-auto px-6 pb-16">
          <h3 className="text-xl font-semibold text-slate-100 mb-6">Related articles</h3>
          <div className="grid sm:grid-cols-3 gap-6">
            {related.map(r => (
              <Link key={r.id} to={`/knowledge/${r.slug}`} className="group block" data-testid={`related-${r.slug}`}>
                <div className="aspect-[4/3] rounded-lg overflow-hidden mb-3 border border-slate-800 bg-slate-900">
                  {r.cover_image ? <img src={r.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full flex items-center justify-center text-slate-700"><BookOpen className="w-8 h-8" /></div>}
                </div>
                <h4 className="text-sm font-semibold text-slate-100 group-hover:text-amber-400 transition-colors line-clamp-2">{r.title}</h4>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default KnowledgeArticle;
